import { Injectable, signal } from '@angular/core';
import { fromEvent, merge, of } from 'rxjs';
import { map, startWith, distinctUntilChanged } from 'rxjs/operators';
import { NotificationService } from '@ieCore/services/notification.service';
import { NO_TITLE } from '@ieCore/consts/notification.consts';
import { TranslateService } from '@ngx-translate/core';

declare const navigator: any;
declare const window: any;

@Injectable({ providedIn: 'root' })
export class NetworkService {
  private manualOnline = signal<boolean | null>(null);
  online = signal<boolean>(navigator.onLine);

  online$ = merge(
    fromEvent(window, 'online').pipe(map(() => true)),
    fromEvent(window, 'offline').pipe(map(() => false)),
    of(navigator.onLine),
  ).pipe(startWith(navigator.onLine), distinctUntilChanged());

  constructor(
    private notificationService: NotificationService,
    private translateService: TranslateService,
  ) {
    this.online$.subscribe((v) => this.online.set(v));
  }

  setOffline() {
    if (this.online()) {
      this.notificationService.error(
        NO_TITLE,
        this.translateService.instant('NO_INTERNET_CONNECTION'),
      );
    }
    this.manualOnline.set(false);
    this.online.set(false);
  }

  /**
   * Called by the HTTP error interceptor when a request fails with status 0 (no
   * response). status 0 also occurs when a single backend service is unreachable,
   * a request is cancelled/aborted (e.g. a component is destroyed mid-flight or a
   * switchMap cancels a prior call), or a CORS failure — none of which mean the
   * user lost connectivity. Only surface the global offline banner when the
   * browser itself reports no connection; genuine offline/online transitions are
   * otherwise covered by the window 'offline'/'online' events in online$.
   */
  setOfflineIfDisconnected() {
    if (!navigator.onLine) {
      this.setOffline();
    }
  }

  setOnline() {
    if (!this.online()) {
      this.notificationService.success(
        NO_TITLE,
        this.translateService.instant('INTERNET_CONNECTION_RESTORED'),
      );
    }
    this.manualOnline.set(true);
    this.online.set(true);
  }
}
